import { Box, type LayoutProps } from "@chakra-ui/react";
import { DemoSuccessContent } from "components/demo-form/demo-success-content";
import { type DemoLinkParameters, useDemoLinkWithParameters } from "hooks/use-demo-link";
import React, { useState } from "react";
import { Button, type ButtonProps } from "ui/button";
import { HubSpotFormModal } from "../hubspot";
import { trackAgentsDemoButtonClicked, trackDemoButtonClicked, trackDemoFormSubmitted } from "./utils";

export type BookDemoButtonProps = {
  /**
   * Button text.
   *
   * @default "Get a demo"
   */
  text?: string;

  /**
   * Button variant.
   *
   * @default "primary"
   */
  variant?: ButtonProps["variant"];

  /**
   * Button pulse.
   */
  pulse?: ButtonProps["pulse"];

  /**
   * Custom Chili Piper router to use with the demo modal.
   *
   * @default "inbound-router"
   */
  chiliPiperRouterName?: string;

  /**
   * If true, uses `inbound-router` with an additional enriched lead field.
   * This allows us to hack the routing logic on the backend to display calendars for AI Agents folks instead of the default routing.
   */
  useAgentsRouter?: boolean; // eslint-disable-line react/boolean-prop-naming

  /**
   * Option to render a link to the /demo page instead of using the demo modal.
   */
  useLink?: "default" | "agents" | undefined;

  /**
   * Custom parameters to use when building the demo link.
   */
  demoLinkParameters?: DemoLinkParameters;
} & Pick<LayoutProps, "display">;

/**
 * By default, provides a button that launches a modal to book a demo.
 *
 * Optionally, provides a button-styled link to the book demo page that tracks the "Demo Button Clicked" conversion event.
 */
export function BookDemoButton({
  text = "Get a demo",
  variant = "primary",
  chiliPiperRouterName = "inbound-router",
  useAgentsRouter = false,
  pulse,
  useLink,
  demoLinkParameters,
  display,
}: BookDemoButtonProps) {
  const defaultDemoLink = useDemoLinkWithParameters(demoLinkParameters);
  const agentsDemoLink = "/demo-ai-agents";

  if (useLink) {
    const link = useLink === "agents" ? agentsDemoLink : defaultDemoLink;

    const handleClick = () => {
      if (useLink === "agents") {
        trackAgentsDemoButtonClicked();
      } else {
        trackDemoButtonClicked();
      }
    };

    return (
      <Button
        variant={variant}
        hasRightChevron={variant === "primary"}
        href={link}
        display={display}
        pulse={pulse}
        onClick={handleClick}
      >
        {text}
      </Button>
    );
  }

  return (
    // Wrap modal in relative position div to avoid UI shifting on click
    <Box pos="relative" display={display}>
      <BookDemoModal
        useAgentsRouter={useAgentsRouter}
        launchButtonVariant={variant}
        label={text}
        chiliPiperRouterName={chiliPiperRouterName}
        launchButtonPulse={pulse}
      />
    </Box>
  );
}

type ModalProps = Pick<BookDemoButtonProps, "useAgentsRouter" | "chiliPiperRouterName"> & {
  label: string;
  launchButtonVariant: ButtonProps["variant"];
  launchButtonPulse?: ButtonProps["pulse"];
};

function BookDemoModal({
  label,
  launchButtonVariant,
  launchButtonPulse,
  chiliPiperRouterName,
  useAgentsRouter = false,
}: ModalProps) {
  const [success, setSuccess] = useState<boolean>(false);
  const [isPrivateBrowser, setIsPrivateBrowser] = useState<boolean>(false);

  const onFormSubmit = (lead: Record<string, string>) => {
    trackDemoFormSubmitted(lead);

    if (window.ChiliPiper) {
      const enrichedLead = useAgentsRouter ? { aiagentdemo: "true", ...lead } : lead;

      window.ChiliPiper.submit?.("hightouch", chiliPiperRouterName, {
        map: true,
        lead: enrichedLead,
        onSuccess() {
          setSuccess(true);
        },
        onError() {
          // Show the success modal even if ChiliPiper errors because the lead is still created.
          setSuccess(true);
        },
      });
    } else {
      // IF CP does not load (ex. private safari windows),
      // we still show the success content to let the user know we have received their request through Hubspot
      setSuccess(true);
      setIsPrivateBrowser(true);
    }
  };

  return (
    <HubSpotFormModal
      label={label}
      formId="6ba03127-8b95-400c-a229-c5fb099d929f"
      sfdcCampaignId="7015e0000010D1QAAU"
      hasLaunchButtonChevron={launchButtonVariant === "primary"}
      launchButtonVariant={launchButtonVariant}
      launchButtonPulse={launchButtonPulse}
      modalLabel="Get a demo"
      modalHeading="Schedule a 30-minute demo with a product expert"
      modalDescription="Learn how Hightouch can solve your use cases."
      successContent={success && <DemoSuccessContent isPrivateBrowser={isPrivateBrowser} />}
      onSubmit={onFormSubmit}
    />
  );
}
